/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Hand a record to a queue, from its detail page.
 *
 * Self-contained -- button, dialog and current-queue chip -- so the detail
 * pages that host it need one line each rather than a copy of this logic.
 *
 * Giving a record to a queue takes it off its current owner. That is the point,
 * and it is worth saying in the dialog rather than letting somebody discover it
 * afterwards: the record disappears from "my leads" the moment they confirm.
 */

import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import { Groups } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";

function AssignToQueue({ objectApiName, recordId, ownerQueueId, onChanged, size }) {
  const { axiosInstance } = useAuth();

  const [open, setOpen] = useState(false);
  const [queues, setQueues] = useState([]);
  const [queueId, setQueueId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");

  const loadQueues = useCallback(async () => {
    try {
      // Only queues scoped to this object can take the record, and the server
      // rejects the rest -- so filtering here turns a 400 into an absence.
      const res = await axiosInstance.get("/call-center/queues", {
        params: { with_stats: false },
      });
      const all = Array.isArray(res.data) ? res.data : [];
      setQueues(all.filter((q) => q.is_active && q.object_api_names?.includes(objectApiName)));
    } catch {
      setQueues([]);
    }
  }, [axiosInstance, objectApiName]);

  useEffect(() => {
    loadQueues();
  }, [loadQueues]);

  const current = queues.find((q) => q.id === ownerQueueId);

  const assign = async () => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post("/work/assign-to-queue", {
        object_api_name: objectApiName,
        record_id: recordId,
        queue_id: Number(queueId),
      });
      setOpen(false);
      setToast("Sent to the queue.");
      if (onChanged) onChanged();
    } catch (err) {
      // 409 means it is already in a queue, and the server's wording explains
      // the one-queue-at-a-time rule better than anything generic would.
      setError(getApiErrorMessage(err, "Could not send it to that queue."));
    } finally {
      setSaving(false);
    }
  };

  if (ownerQueueId) {
    return (
      <Tooltip title="Waiting in this queue. It has no individual owner until somebody accepts it.">
        <Chip size={size || "small"} color="info" icon={<Groups />} label={
          current ? `In ${current.name}` : "In a queue"
        } />
      </Tooltip>
    );
  }

  if (queues.length === 0) return null;

  return (
    <>
      <MDButton
        variant="outlined"
        color="info"
        size={size || "small"}
        startIcon={<Groups />}
        onClick={() => setOpen(true)}
      >
        Send to queue
      </MDButton>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Send to a queue</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" mb={1}>
            It will stop being owned by anyone in particular and wait for a member of the queue to
            accept it.
          </MDTypography>
          {error ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              {error}
            </Alert>
          ) : null}
          <TextField
            select
            fullWidth
            size="small"
            label="Queue"
            value={queueId}
            onChange={(e) => setQueueId(e.target.value)}
            margin="normal"
          >
            {queues.map((q) => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
                {q.pending_items ? ` · ${q.pending_items} waiting` : ""}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <MDButton variant="text" color="dark" onClick={() => setOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            disabled={!queueId || saving}
            onClick={assign}
          >
            Send
          </MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}

AssignToQueue.propTypes = {
  objectApiName: PropTypes.string.isRequired,
  recordId: PropTypes.number.isRequired,
  ownerQueueId: PropTypes.number,
  onChanged: PropTypes.func,
  size: PropTypes.string,
};

AssignToQueue.defaultProps = {
  ownerQueueId: null,
  onChanged: null,
  size: "small",
};

export default AssignToQueue;
